import { useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import {
  IssuerAddUpdateAssetModal,
  IssuerAddUpdateAssetModalProps,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateAssetModal.tsx';
import { IssuerAddUpdateBankModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateBankModal.tsx';
import { IssuerPortfolioAssetFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAssetFullInfoModal.tsx';
import { ModalBrokerAddUpdate } from '@/modules/issuer-dashboard/components/portfolios/ModalBrokerAddUpdate.tsx';
import { useIssuerDeleteBankMutation } from '@/modules/issuer-dashboard/queries/banks.ts';
import {
  useIssuerAllAssetsInfiniteListQuery,
  useIssuerDeletePortfolioAssetMutation,
  useIssuerDeletePortfolioBrokerMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon, InfoCircleIcon, WarningYellowIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatNumberCustom } from 'ui/utils';
import { AllAssetExtended, AssetExtended, BrokerExtended } from 'api';

const tableHeadItems: TableHeadColumn[] = [
  { title: 'Asset' },
  { title: 'Type' },
  { title: 'Industry' },
  {
    title: (
      <div className="flex items-center gap-1">
        <span>Price per share</span>
        <TooltipNew
          text="Prices of publicly traded assets were updated automatically using publicly available information. Please double-check."
          place="top"
          showDelay={200}
          hideDelay={300}
          classNameTooltip="text-xs text-center max-w-56"
        >
          <img src={InfoCircleIcon} alt="info" className="w-3.5 h-3.5 min-w-3.5 cursor-pointer" />
        </TooltipNew>
      </div>
    ),
  },
  { title: 'Quantity' },
  { title: 'Total asset value' },
  { title: 'Currency' },
  { title: '% of portfolio value' },
  { title: 'PNL' },
  { title: '' },
];

export interface PortfolioAssetsTabProps {
  portfolioId: number;
  outdatedAssetIds?: Set<number>;
}

export const PortfolioAssetsTab = ({ portfolioId, outdatedAssetIds }: PortfolioAssetsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { isMobile } = useIsMobile();
  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    data: dataList,
    isFetching,
  } = useIssuerAllAssetsInfiniteListQuery({
    page: 1,
    page_size: 30,
    portfolio_id: portfolioId,
  });

  const handleOpenFullInfoModal = useCallback(
    (item: AllAssetExtended) => {
      if (!item.asset_id) return;
      openModal(IssuerPortfolioAssetFullInfoModal, {
        id: item.asset_id,
        name: item.name,
        portfolioId,
      });
    },
    [openModal, portfolioId],
  );

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataList?.pages?.forEach(page => {
      result = result.concat(
        page.data
          // ?.filter(item => !item.is_broker)
          ?.map(item => {
            const {
              name,
              asset_type,
              industry,
              price_per_share,
              quantity,
              total_asset_value,
              percent_portfolio_value,
              currency,
              kind,
              source_name,
            } = item;
            const isAsset = kind === 'asset';
            const sourceType = kind === 'bank_cash' ? 'Bank' : 'Broker';
            const body: BodyCell[] = [
              {
                cell: (
                  <div className="flex items-center gap-3">
                    <div>
                      <div className={isAsset ? 'text-blue-700' : undefined}>{name}</div>
                      {source_name && (
                        <div className="text-xs text-gray-500">
                          {sourceType} · {source_name}
                        </div>
                      )}
                    </div>
                    {isAsset && <CreatedByAiLabel createdWithAi={false} />}
                  </div>
                ),
              },
              {
                cell: (
                  <div>
                    {isAsset
                      ? asset_type || '-'
                      : kind === 'bank_cash'
                      ? 'Bank cash'
                      : 'Broker cash'}
                  </div>
                ),
              },
              { cell: <div>{isAsset ? industry || '-' : '-'}</div> },
              {
                cell: isAsset ? (
                  <div className="flex items-center gap-1.5">
                    <span>
                      {goodFormatNumberCustom(price_per_share || 0, currency?.toUpperCase(), 2, 6)}
                    </span>
                    {!Number(price_per_share) ? (
                      <TooltipNew
                        text="This asset has not been priced yet. To set a price, click Edit on the asset."
                        place="top-end"
                        showDelay={200}
                        hideDelay={300}
                        classNameTooltip="text-xs text-center"
                      >
                        <img src={WarningYellowIcon} alt="not priced" className="w-4 h-4 min-w-4" />
                      </TooltipNew>
                    ) : (
                      outdatedAssetIds?.has(item.asset_id || 0) && (
                        <TooltipNew
                          text="Price hasn't been updated in 30+ days. To update it, click Edit on the asset."
                          place="top-end"
                          showDelay={200}
                          hideDelay={300}
                          classNameTooltip="text-xs text-center"
                        >
                          <img
                            src={WarningYellowIcon}
                            alt="outdated price"
                            className="w-4 h-4 min-w-4"
                          />
                        </TooltipNew>
                      )
                    )}
                  </div>
                ) : (
                  '-'
                ),
              },
              { cell: isAsset ? goodFormatNumber(quantity || 0) : '-' },
              {
                cell: goodFormatNumberCustom(total_asset_value || 0, currency?.toUpperCase(), 0, 2),
              },
              { cell: currency?.toUpperCase() },
              {
                cell: <div>{goodFormatNumber(percent_portfolio_value || 0)}%</div>,
              },
              { cell: '-' },
              {
                cell: isAsset ? (
                  <MoreButtonAsset item={item} portfolioId={portfolioId} />
                ) : item.kind === 'bank_cash' ? (
                  <BankCashMoreButton item={item} />
                ) : (
                  <BrokerCashMoreButton item={item} portfolioId={portfolioId} />
                ),
              },
            ];

            return {
              cells: body,
              onClick: isAsset ? () => handleOpenFullInfoModal(item) : undefined,
            };
          }),
      );
    });
    return result;
  }, [dataList, handleOpenFullInfoModal, portfolioId, outdatedAssetIds]);

  const handleOpenAddAssetModal = useCallback(() => {
    openModal(IssuerAddUpdateAssetModal, {
      portfolioId,
    });
  }, [openModal, portfolioId]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full">
          {isFetching && <Spinner size="md" variant="primary" />}
        </div>
        <div className="w-[115px]">
          <Button className="w-auto" onClick={handleOpenAddAssetModal}>
            New asset
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={tableHeadItems}
        items={tableItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export interface MoreButtonAssetProps {
  item: AllAssetExtended;
  portfolioId: number;
}

export const MoreButtonAsset = ({ item, portfolioId }: MoreButtonAssetProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioAssetMutation();
  const itemsAsset = useMemo<DropdownMenuItem[]>(() => {
    const asset = {
      ...item,
      id: item.asset_id,
      portfolio_id: portfolioId,
    } as unknown as AssetExtended;
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal<IssuerAddUpdateAssetModalProps>(IssuerAddUpdateAssetModal, {
            item: asset,
            portfolioId: portfolioId,
          });
        },
      },
      {
        label: 'Archive',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Archive {item.name}</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.asset_id!);
            },
          });
        },
      },
    ];
  }, [item, mutateAsyncDelete, openModal, portfolioId]);

  return (
    <div>
      <DropdownMenu
        items={itemsAsset}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        autoPlace
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};

const BrokerCashMoreButton = ({ item, portfolioId }: MoreButtonAssetProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioBrokerMutation();
  const items = useMemo<DropdownMenuItem[]>(() => {
    const broker = {
      id: item.broker_id,
      name: item.source_name,
      portfolio_id: portfolioId,
    } as unknown as BrokerExtended;
    return [
      {
        label: 'Edit',
        onClick: () => openModal(ModalBrokerAddUpdate, { item: broker, portfolioId }),
      },
      {
        label: 'Archive',
        onClick: () =>
          openModal(ConfirmModal, {
            title: <span>Archive {broker.name}</span>,
            content: 'This action is irreversible.',
            onConfirm: async () => {
              await mutateAsyncDelete(broker.id);
            },
          }),
      },
    ];
  }, [item, mutateAsyncDelete, openModal, portfolioId]);
  return (
    <DropdownMenu
      items={items}
      button={
        <button className="flex items-center justify-center">
          <img
            alt="More..."
            src={DotsHorizontalIcon}
            className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
          />
        </button>
      }
      placement="left-start"
      autoPlace
      className="w-[180px]"
      containerClassName="max-w-6"
    />
  );
};

const BankCashMoreButton = ({ item }: { item: AllAssetExtended }) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeleteBankMutation();
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const items = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: 'Edit',
        onClick: async () => {
          const response = await api.v1.getRegisterBankByIdV1SrRegisterBanksRegisterBankIdGet(
            { registerBankId: item.bank_id! },
            { headers: { 'xRegister-ID': registerId } },
          );
          openModal(IssuerAddUpdateBankModal, { item: response.data.register_bank });
        },
      },
      {
        label: 'Archive',
        onClick: () =>
          openModal(ConfirmModal, {
            title: <span>Archive {item.source_name}</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.bank_id!);
            },
          }),
      },
    ],
    [item, mutateAsyncDelete, openModal, registerId],
  );
  return (
    <DropdownMenu
      items={items}
      button={
        <button className="flex items-center justify-center">
          <img
            alt="More..."
            src={DotsHorizontalIcon}
            className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
          />
        </button>
      }
      placement="left-start"
      autoPlace
      className="w-[180px]"
      containerClassName="max-w-6"
    />
  );
};
